import { Loader } from '@snowball-tech/fractal/dist/src/components/Loader'
import { Progress } from '@snowball-tech/fractal/dist/src/components/Progress'
import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'
import { cj } from '@snowball-tech/fractal/dist/src/styles/helpers'
import { UilImage as ImageIcon } from '@tooni/iconscout-unicons-react'
import { filesize } from 'filesize'

import isNumber from 'lodash/fp/isNumber'

import type { Progress as ProgressType } from '../../../api/endpoints'
import type { Translator } from '../../../types'

import useTranslations from '../../../hooks/useTranslations'

type Props = {
  isLoading?: boolean
  label?: string
  progress?: ProgressType | null
  small?: boolean
}
export default function AddImageButton({
  isLoading = false,
  label,
  progress = null,
  small = false,
}: Props) {
  let $t: Translator | null = null
  // When using this component in the BlockNote editor it randomly happens that
  // React complains about the NextIntl context not being found, though the
  // translation is working fine.
  // To avoid the error in the console, simply silently swallow it.
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    $t = useTranslations()
  } catch {
    // Nothing to do here.
  }

  return (
    <div
      className={cj(
        'flex items-center px-1',
        small ? 'flex-row gap-1' : 'flex-col justify-center',
        isLoading ? 'flex-col justify-center py-2 gap-0' : 'flex-row',
      )}
    >
      {isLoading ? (
        <>
          <Loader />

          {progress !== null && (
            <>
              <div className="flex w-full flex-row flex-nowrap items-center gap-1">
                <Progress value={progress.percentage} />

                <Typography
                  className="whitespace-nowrap text-nowrap"
                  variant="body-2-median"
                >
                  {progress.percentage.toFixed(2)}%
                </Typography>
              </div>

              <Typography variant="caption-median">
                {filesize(progress.loaded ?? 0, {
                  locale: true,
                  symbols: {
                    B: $t?.('general.units.file_size.B'),
                    GB: $t?.('general.units.file_size.GB'),
                    kB: $t?.('general.units.file_size.kB'),
                    MB: $t?.('general.units.file_size.MB'),
                    TB: $t?.('general.units.file_size.TB'),
                  },
                })}
                {isNumber(progress.total) ? '/' : ''}
                {isNumber(progress.total)
                  ? filesize(progress.total ?? 0, {
                      locale: true,
                      symbols: {
                        B: $t?.('general.units.file_size.B'),
                        GB: $t?.('general.units.file_size.GB'),
                        kB: $t?.('general.units.file_size.kB'),
                        MB: $t?.('general.units.file_size.MB'),
                        TB: $t?.('general.units.file_size.TB'),
                      },
                    })
                  : ''}
              </Typography>
            </>
          )}
        </>
      ) : (
        <>
          <ImageIcon className={cj(small ? 'size-5' : 'size-10')} />

          <Typography variant={small ? 'body-2-median' : 'body-1-median'}>
            {label ?? $t?.('editor.image.loader.placeholder_text')}
          </Typography>
        </>
      )}
    </div>
  )
}
